import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { ChevronLeft, ChevronRight, Search } from 'lucide-react';

import TalentCard from './TalentCard';
import Loader from 'components/Loader/Loader';
import styles from './TalentPoolCarousel.module.scss';
import NoApplicantsEmptyState from '../NoApplicantsEmptyState';

const getCardsPerPage = () => {
  if (typeof window === 'undefined') return 4;
  const width = window.innerWidth;
  if (width < 768) return 1;
  if (width < 1024) return 2;
  return 4;
};

const BrowseMoreCard = () => (
  <a
    href="/browse"
    className={styles.browseCard}
    aria-label="Browse for more experts to add to your talent pool"
  >
    <div className={styles.browseIconWrapper}>
      <Search width={32} height={32} className={styles.browseIcon} />
    </div>
    <p className={styles.browseTitle}>Browse for more experts</p>
    <p className={styles.browseSubtitle}>to invite to your job</p>
    <span className={styles.browseButton}>Browse experts</span>
  </a>
);

const TalentPoolCarousel = ({
  users,
  collaborations,
  onInvite,
  loadingTalentPool,
}) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [direction, setDirection] = useState(null);
  const [nextPage, setNextPage] = useState(null);
  const [cardsPerPage, setCardsPerPage] = useState(getCardsPerPage());

  const usersWithBrowseCard = [
    ...(users || []),
    { id: 'browse-more', isBrowseCard: true },
  ];
  const totalPages = Math.ceil(usersWithBrowseCard.length / cardsPerPage);

  useEffect(() => {
    const handleResize = () => {
      const newCardsPerPage = getCardsPerPage();
      if (newCardsPerPage !== cardsPerPage) {
        setCardsPerPage(newCardsPerPage);
        setCurrentPage(0);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [cardsPerPage]);

  useEffect(() => {
    if (isAnimating && nextPage !== null) {
      const timer = setTimeout(() => {
        setCurrentPage(nextPage);
        setIsAnimating(false);
        setNextPage(null);
        setDirection(null);
      }, 400);
      return () => clearTimeout(timer);
    }
  }, [isAnimating, nextPage]);

  const handlePrevious = () => {
    if (isAnimating) return;
    const pages = Math.ceil(usersWithBrowseCard.length / cardsPerPage);
    const newPage = currentPage === 0 ? pages - 1 : currentPage - 1;
    setDirection('left');
    setNextPage(newPage);
    setIsAnimating(true);
  };

  const handleNext = () => {
    if (isAnimating) return;
    const pages = Math.ceil(usersWithBrowseCard.length / cardsPerPage);
    const newPage = currentPage === pages - 1 ? 0 : currentPage + 1;
    setDirection('right');
    setNextPage(newPage);
    setIsAnimating(true);
  };

  const handleDotClick = (pageIndex) => {
    if (isAnimating || pageIndex === currentPage) return;
    setDirection(pageIndex > currentPage ? 'right' : 'left');
    setNextPage(pageIndex);
    setIsAnimating(true);
  };

  const getCurrentUsers = (page) => {
    const startIndex = page * cardsPerPage;
    const endIndex = startIndex + cardsPerPage;
    return usersWithBrowseCard.slice(startIndex, endIndex);
  };

  const currentUsers = getCurrentUsers(currentPage);
  const nextUsers = nextPage !== null ? getCurrentUsers(nextPage) : null;
  const shouldShowCarousel = usersWithBrowseCard.length > cardsPerPage;

  if (loadingTalentPool) {
    return <Loader loading />;
  }

  if (!loadingTalentPool && (!users || users.length === 0)) {
    return (
      <NoApplicantsEmptyState tab="talentPool" browseURL="/browse" compact />
    );
  }

  return (
    <div className={styles.carouselContainer}>
      <div className={styles.carouselNavRow}>
        {shouldShowCarousel && (
          <button
            className={styles.navButton}
            onClick={handlePrevious}
            aria-label="Previous page"
          >
            <ChevronLeft width={24} height={24} />
          </button>
        )}

        <div className={styles.cardsWrapper}>
          <div
            className={`${styles.carouselTrack} ${
              isAnimating ? styles.carouselTrackAnimating : ''
            }`}
          >
            <div
              className={`${styles.talentCardsGrid} ${styles.currentSlide} ${
                isAnimating
                  ? direction === 'right'
                    ? styles.slideOutLeft
                    : styles.slideOutRight
                  : ''
              }`}
            >
              {currentUsers.map((user) =>
                user.isBrowseCard ? (
                  <BrowseMoreCard key="browse-more" />
                ) : (
                  <TalentCard
                    key={user.id}
                    user={user}
                    collaborations={collaborations}
                    onInvite={onInvite}
                  />
                )
              )}
            </div>
            {isAnimating && nextUsers && (
              <div
                className={`${styles.talentCardsGrid} ${styles.nextSlide} ${
                  direction === 'right'
                    ? styles.slideInRight
                    : styles.slideInLeft
                }`}
              >
                {nextUsers.map((user) =>
                  user.isBrowseCard ? (
                    <BrowseMoreCard key="browse-more" />
                  ) : (
                    <TalentCard
                      key={`next-${user.id}`}
                      user={user}
                      collaborations={collaborations}
                      onInvite={onInvite}
                    />
                  )
                )}
              </div>
            )}
          </div>
        </div>

        {shouldShowCarousel && (
          <button
            className={styles.navButton}
            onClick={handleNext}
            aria-label="Next page"
          >
            <ChevronRight width={24} height={24} />
          </button>
        )}
      </div>

      {shouldShowCarousel && (
        <div className={styles.pagination}>
          {Array.from({ length: totalPages }).map((_, index) => (
            <button
              key={index}
              className={`${styles.paginationDot} ${
                index ===
                (isAnimating && nextPage !== null ? nextPage : currentPage)
                  ? styles.paginationDotActive
                  : ''
              }`}
              onClick={() => handleDotClick(index)}
              aria-label={`Go to page ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

TalentPoolCarousel.propTypes = {
  users: PropTypes.arrayOf(PropTypes.object).isRequired,
  collaborations: PropTypes.arrayOf(PropTypes.object),
  onInvite: PropTypes.func.isRequired,
  loadingTalentPool: PropTypes.bool,
};

TalentPoolCarousel.defaultProps = {
  collaborations: [],
  loadingTalentPool: false,
};

export default TalentPoolCarousel;
